import { Component, Input, OnInit } from '@angular/core';
import * as d3 from 'd3';

@Component({
  selector: 'app-stl-bar-chart-consumptions',
  template: `
    <div id="legend"></div>
    <figure id="bar-consumptions"></figure>    
  `,
  styles: [
  ]
})
export class StlBarChartConsumptionsComponent {
  @Input() data: any;
  
  private svg: any;
  private margin = 32;
  private width = 340 - (this.margin * 2);
  private height = 280 - (this.margin * 2);

  public yScale: any;
  public xScale: any;

  constructor() { }

  ngOnInit(): void {
    this.createSvg();
    this.drawChart(this.data);
  }

  private createSvg(): void {
    const myElement = document.getElementById('cardConsumptions');
    if (myElement) {
      this.width = myElement.getBoundingClientRect().width - 60;
    }

    this.svg = d3.select("figure#bar-consumptions")
    .append("svg")
    .attr("width", '100%')
    .attr("height", this.height + (this.margin * 2))
    .append("g")
    .attr("transform", "translate(" + this.margin + "," + this.margin + ")");
  }

  simpleGroup(array: any[], key: string) {
    const groupedData = array.reduce((acc, obj) => {
      const keyValue = obj[key];
      if (!acc[keyValue]) {
        acc[keyValue] = [];
      }
      acc[keyValue].push(obj);
      return acc;
    }, {});
  
    return { keys: Object.keys(groupedData), data: groupedData };
  }

  groupBy(array1: any[], array2: any[], key: string) {
    return array1.reduce((acc, obj) => {
        const groupKey = obj[key];
        const correspondingObj = array2.find(item => item[key] === groupKey);
        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        const combinedObj = { ...obj, ...correspondingObj };
        // delete combinedObj[key];
        acc[groupKey].push(combinedObj);
        return acc;
    }, {});
  }

  private drawChart(data: any): void {
    let dates = data.consumptions.map((d: any) => {
      return {
        "liters": d["liters"],
        "month": d["month"],
        "year": d["year"],
        "week": d["week"],
        "tanque": d["tanque_id"],
        // Create a sortable string in YYYY-MM-WW format
        "sortKey": `${d["year"]}-${String(d["month"]).padStart(2, '0')}-${String(d["week"]).padStart(2, '0')}`
      };
    });

    let readings = data.readings.map((d: any) => {
      return {
        "litersRead": d["litersread"],
        "month": d["month"],
        "year": d["year"],
        "week": d["week"],
        "tanque": d["tanque_id"],
        "settlement_id": d["settlement_id"],
        // Create a sortable string in YYYY-MM-WW format
        "sortKey": `${d["year"]}-${String(d["month"]).padStart(2, '0')}-${String(d["week"]).padStart(2, '0')}`
      };
    });

    // Sort both arrays by the sortKey
    dates.sort((a: any, b: any) => a.sortKey.localeCompare(b.sortKey));
    readings.sort((a: any, b: any) => a.sortKey.localeCompare(b.sortKey));

    const groupedData = this.groupBy(readings, dates, 'sortKey');
    const groupdWeeks = this.simpleGroup(readings, 'sortKey');
    let color = d3.scaleOrdinal(d3.schemeCategory10);
    var subgroups = ['litersRead', 'liters'];

    // Create display labels for x-axis
    const xAxisLabels = Array.from(new Set(dates.map((d: any) => d.sortKey)))
      .sort()
      .map((key: any) => {
        const [year, month, week] = key.split('-');
        return `${month}/${year.slice(2)}-W${week}`;
      });

    const x = d3.scaleBand()
      .range([0, this.width])
      .domain(xAxisLabels)
      .padding(0.2);

    // Draw the X-axis on the DOM with rotated labels
    this.svg.append("g")
      .attr("transform", "translate(0," + this.height + ")")
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "translate(-10,0)rotate(-45)")
      .style("text-anchor", "end")
      .style("font-size", "12px");

    var xSubgroup = d3.scaleBand()
      .domain(subgroups)
      .range([0, x.bandwidth()])
      .padding(0.05);

    let yMax = Math.max(
      d3.max(dates, (d: any) => Number(d.liters) || 0) || 0,
      d3.max(readings, (d: any) => Number(d.litersRead) || 0) || 0
    );

    yMax = yMax * 1.1;

    const y = d3.scaleLinear()
      .domain([0, yMax])
      .range([this.height, 0]);

    // Add Y-axis
    this.svg.append("g")
      .call(d3.axisLeft(y)
        .tickFormat((d: any) => `${d/1000}K`)
        .ticks(5));

    const ctl = this;

    var tooltip = d3.select("figure#bar-consumptions")
      .append("div")
      .style("position", "absolute")
      .style("z-index", "10000")
      .style("visibility", "hidden")
      .style("background-color", "white")
      .style("border", "solid")
      .style("border-width", "1px")
      .style("border-radius", "5px")
      .style("padding", "10px");

    // Draw bars
    groupdWeeks.keys.forEach((key: any) => {
      const displayKey = (() => {
        const [year, month, week] = key.split('-');
        return `${month}/${year.slice(2)}-W${week}`;
      })();

      this.svg.append("g")
        .selectAll("g")
        .data(groupedData[key])
        .enter()
        .append("g")
        .attr("transform", function(d: any) { 
          return "translate(" + (x(displayKey) || 0) + ",0)"; 
        })
        .selectAll("rect")
        .data(function(d: any) { 
          return subgroups.map((key) => {
            return {
              key: key, 
              value: d[key] || 0,
              originalData: d
            }; 
          }); 
        })
        .enter().append("rect")
        .attr("x", function(d: any) { return xSubgroup(d.key) || 0; })
        .attr("y", function(d: any) { return y(d.value); })
        .attr("width", xSubgroup.bandwidth())
        .attr("height", function(d: any) { return ctl.height - y(Number(d.value) || 0); })
        .attr("fill", function(d: any) { return color(d.key); })
        .on("mouseover", function(event: any, d: any) {
          tooltip
            .style("visibility", "visible")
            .html(`${d.key}: ${d.value.toFixed(2)} L`)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 10) + "px");
        })
        .on("mouseout", function() {
          tooltip.style("visibility", "hidden");
        });
    });

    // Add spline curves on top of bars
    const lineGenerator = d3.line<any>()
      .x((d: any) => (x(d.displayKey) || 0) + x.bandwidth() / 2)
      .y((d: any) => y(d.value))
      .curve(d3.curveCardinal);

    // Prepare data for lines
    const litersData = dates.map((d: any) => ({
      displayKey: `${String(d.month).padStart(2, '0')}/${String(d.year).slice(2)}-W${String(d.week).padStart(2, '0')}`,
      value: d.liters || 0
    }));

    const litersReadData = readings.map((d: any) => ({
      displayKey: `${String(d.month).padStart(2, '0')}/${String(d.year).slice(2)}-W${String(d.week).padStart(2, '0')}`,
      value: d.litersRead || 0
    }));

    // Draw splines
    this.svg.append("path")
      .datum(litersData)
      .attr("fill", "none")
      .attr("stroke", color("liters"))
      .attr("stroke-width", 2)
      .attr("d", lineGenerator);

    this.svg.append("path")
      .datum(litersReadData)
      .attr("fill", "none")
      .attr("stroke", color("litersRead"))
      .attr("stroke-width", 2)
      .attr("d", lineGenerator);

    // Add legend
    const legend = this.svg.append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 10)
      .attr("text-anchor", "start")
      .selectAll("g")
      .data(subgroups)
      .enter().append("g")
      .attr("transform", (d: any, i: any) => `translate(0,${i * 20})`);

    legend.append("rect")
      .attr("x", this.width - 19)
      .attr("width", 19)
      .attr("height", 19)
      .attr("fill", color);

    legend.append("text")
      .attr("x", this.width - 24)
      .attr("y", 9.5)
      .attr("dy", "0.32em")
      .text((d: any) => d);
  }
}
